import React, { useEffect, useRef } from "react";
import uuid from "uuid/v4";
import PropTypes from "prop-types";
import Loading from "../elements/loading";
import TableRow from "./table-row";
import classNames from "classnames";
import Pagination from "../forms/pagination";
import EmptyStateMessage from "../elements/empty-state-message";
import useInfiniteScroll from "../../hooks/use-infinite-scroll";

const Table = ({
  data,
  columns,
  caption,
  loading,
  total,
  limit,
  page,
  handlePageChange,
  getTrProps,
  tableClassName,
  tableFlexBasis,
  emptyStateMessageText = "Not available",
  emptyStateMessageSubtext = "",
  emptyStateActionLabel = "",
  handleEmptyStateAction,
  emptyStateActionButton,
  scrollFetch = () => {},
  scrollToTop = false,
}) => {
  const bodyRef = useRef();
  useInfiniteScroll(scrollFetch, bodyRef.current);

  useEffect(() => {
    if (scrollToTop) {
      bodyRef.current.scrollTop = 0;
    }
  }, [scrollToTop]);

  const handlePageOnChange = (page) => {
    handlePageChange(page);
    bodyRef.current.scrollTop = 0;
  };

  const handleEmptyStateOnAction = () => {
    handleEmptyStateAction(window);
  };

  return (
    <table className={classNames("et--flex-table--wrapper", tableClassName)}>
      <caption className="et--visually-hidden">{caption}</caption>
      <thead>
        <tr style={{ overflowY: "auto" }} className="et--flex-table--header-row">
          {columns.map(({ width, header, headerClassName, headerStyle }) => (
            <th
              key={uuid()}
              style={{
                minWidth: width,
                ...headerStyle,
              }}
              className={classNames("et--flex-table--header-cell", headerClassName)}
            >
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody
        ref={bodyRef}
        style={{ overflowY: "auto", minHeight: loading ? "10vh" : "0", flexBasis: tableFlexBasis }}
        className="et--flex-table--body"
      >
        {loading ? (
          <tr>
            <td>
              <Loading />
            </td>
          </tr>
        ) : !data.length ? (
          <tr className="et--flex-table--empty-state">
            <td>
              <EmptyStateMessage
                text={emptyStateMessageText}
                subtext={emptyStateMessageSubtext}
                actionLabel={emptyStateActionLabel}
                onAction={handleEmptyStateOnAction}
                actionButton={emptyStateActionButton}
              />
            </td>
          </tr>
        ) : (
          data.map((datum, idx) => {
            const tr_props = typeof getTrProps === "function" ? getTrProps(datum, idx) : {};
            return (
              <TableRow key={uuid()} {...tr_props}>
                {columns.map((col) => {
                  const { Cell } = col;
                  return Cell ? (
                    <Cell key={uuid()} className={classNames("et--flex-table--cell", col.cellClassName)} {...datum} />
                  ) : (
                    <td
                      key={uuid()}
                      style={{ minWidth: col.width }}
                      className={classNames("et--flex-table--cell", col.cellClassName)}
                    >
                      {typeof col.accessor === "function" ? col.accessor(datum, idx) : datum[col.accessor]}
                    </td>
                  );
                })}
              </TableRow>
            );
          })
        )}
      </tbody>

      {typeof handlePageChange === "function" && total > limit && (
        <tfoot className="et--flex-table--pagination">
          <tr>
            <td className="et--flex-table--pagination-holder">
              <Pagination page={page} total={Math.ceil(total / limit)} onChange={handlePageOnChange} />
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  );
};

Table.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  caption: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  total: PropTypes.number,
  limit: PropTypes.number,
  page: PropTypes.number,
  handlePageChange: PropTypes.func,
  getTrProps: PropTypes.func,
  emptyStateMessageText: PropTypes.string,
  emptyStateMessageSubtext: PropTypes.string,
  emptyStateActionLabel: PropTypes.string,
  handleEmptyStateAction: PropTypes.func,
  emptyStateActionButton: PropTypes.object,
  disableScrollTopOnPageChange: PropTypes.bool,
  tableFlexBasis: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tableClassName: PropTypes.string,
  scrollFetch: PropTypes.func,
  scrollToTop: PropTypes.bool,
};

export default Table;
